import {
  SpecialPeriodSchema,
  SpecialPeriodType,
} from '../../../backend/src/services/resources/special-periods/special-period.types'
import { SpecialPeriodRepositoryCreationPayload } from '../../../backend/src/services/resources/special-periods/special-periods.repository.service.types'
import api, { ApiResponse } from '.'
import { Organization } from './organizations'

/**
 * Special period interface extending backend schema
 */
export interface SpecialPeriod extends Omit<SpecialPeriodSchema, 'employer' | '_id'> {
  _id: string
  employer: string | Organization
}

/**
 * Payload for creating a special period
 * employer will be added automatically from session or provided explicitly
 */
export type SpecialPeriodCreationPayload = Omit<SpecialPeriodRepositoryCreationPayload, 'employer'>

/**
 * Payload for updating a special period
 */
export interface SpecialPeriodUpdatePayload {
  id: string
  type?: string
  name?: string
  startDate?: string // ISO 8601 format
  endDate?: string // ISO 8601 format
}

/**
 * Query parameters for listing special periods
 */
export interface SpecialPeriodListParams {
  employer?: string
  type?: SpecialPeriodType
  startDate?: string // ISO 8601 format
  endDate?: string // ISO 8601 format
  sort?: string
  limit?: number
  offset?: number
}

/**
 * Fetch all special periods with optional filters
 */
export const fetch = async (
  params?: SpecialPeriodListParams,
): Promise<ApiResponse<SpecialPeriod[]>> =>
  await api({
    endpoint: '/special-periods',
    method: 'get',
    params: {
      sort: '-startDate',
      ...(params || {}),
    },
  })

/**
 * Get a single special period by ID
 */
export const get = async (id: string): Promise<ApiResponse<SpecialPeriod>> =>
  await api({
    endpoint: `/special-periods/${id}`,
    method: 'get',
    params: {
      _id: id,
    },
  })

/**
 * Create a new special period
 */
export const create = async (
  params: SpecialPeriodCreationPayload,
): Promise<ApiResponse<SpecialPeriod>> =>
  await api({
    endpoint: '/special-periods',
    method: 'post',
    params,
  })

/**
 * Update an existing special period
 */
export const update = async (
  params: SpecialPeriodUpdatePayload,
): Promise<ApiResponse<SpecialPeriod>> => {
  const { id, ...payload } = params
  return await api({
    endpoint: `/special-periods/${id}`,
    method: 'put',
    params: {
      _id: id,
      ...payload,
    },
  })
}

/**
 * Find distinct types of special periods for a given employer
 */
export const findDistinctTypes = async (employerId: string): Promise<ApiResponse<string[]>> =>
  await api({
    endpoint: '/special-periods/types',
    method: 'get',
    params: {
      employer: employerId,
    },
  })
